import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Loader2, Check, X, RefreshCw, User, Target, Briefcase, Mic } from 'lucide-react';
import { Button } from './ui/button';
import DISCBadge from './DISCBadge';
import DOMPurify from 'dompurify';

const API = process.env.REACT_APP_AI_BFF_URL || process.env.REACT_APP_BACKEND_URL || '';

export default function AIEmailWriter({ onInsert, onClose, contactId, dealId, accountId, existingDraft }) {
    const { getAuthHeader, user } = useAuth();
    const [prompt, setPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [editSubject, setEditSubject] = useState('');
    const [streamingResult, setStreamingResult] = useState(null);

    // Context badges
    const [contactInfo, setContactInfo] = useState(null);
    const [hasVoiceProfile, setHasVoiceProfile] = useState(false);

    useEffect(() => {
        // Check for context availability
        const checkContext = async () => {
            try {
                if (contactId) {
                    const res = await axios.get(`${API}/api/contacts/${contactId}/disc`, { headers: getAuthHeader() });
                    setContactInfo(res.data);
                }
            } catch { /* ignore */ }
            try {
                const res = await axios.get(`${API}/api/voice-profile/md`, { headers: getAuthHeader() });
                setHasVoiceProfile(!!(res.data?.md_content));
            } catch { /* ignore */ }
        };
        checkContext();
    }, [contactId, getAuthHeader]);

    const handleGenerate = async () => {
        if (!prompt.trim() && !existingDraft) {
            toast.error('Please describe what you want to write');
            return;
        }
        setLoading(true);
        setResult(null);
        setStreamingResult(null);

        const payload = {
            prompt: prompt.trim(),
            contact_id: contactId || undefined,
            deal_id: dealId || undefined,
            account_id: accountId || undefined,
            existing_draft: existingDraft || undefined,
        };

        try {
            // Try BFF streaming endpoint first
            const bffUrl = process.env.REACT_APP_AI_BFF_URL;
            if (bffUrl) {
                const headers = getAuthHeader();
                const res = await fetch(`${bffUrl}/ai/write-email`, {
                    method: 'POST',
                    headers: { ...headers, 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                if (res.ok && res.body) {
                    const reader = res.body.getReader();
                    const decoder = new TextDecoder();
                    let fullText = '';

                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        fullText += decoder.decode(value, { stream: true });

                        // Try to parse as JSON progressively
                        try {
                            const parsed = JSON.parse(fullText);
                            setStreamingResult(parsed);
                            if (parsed.subject && !editSubject) setEditSubject(parsed.subject);
                        } catch {
                            // Partial JSON, try extracting what we can
                        }
                    }

                    // Final parse
                    try {
                        const final = JSON.parse(fullText);
                        setResult(final);
                        setEditSubject(final.subject || '');
                        setStreamingResult(null);
                    } catch {
                        // If streaming object didn't produce valid JSON, try the chunks
                        toast.error('Failed to parse AI response');
                    }
                    setLoading(false);
                    return;
                }
            }

            // Fallback to Python backend
            const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/ai/write-email`, payload, { headers: getAuthHeader() });
            setResult(res.data);
            setEditSubject(res.data.subject || '');
        } catch (err) {
            toast.error('Failed to generate email');
        } finally {
            setLoading(false);
        }
    };

    const handleInsert = () => {
        if (onInsert && result) {
            onInsert({ subject: editSubject || result.subject, body: result.body });
        }
        onClose();
    };

    const handleRegenerate = () => {
        setResult(null);
    };

    // ─── Prompt State ───────────────────────────────────────────────────────
    if (!result && !loading) {
        return (
            <div className="bg-stone-50 border-b border-stone-200 p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        <span className="text-sm font-medium text-stone-700">AI Email Writer</span>
                    </div>
                    <button onClick={onClose} className="p-1 text-stone-400 hover:text-stone-600" type="button">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <textarea
                    value={prompt}
                    onChange={e => setPrompt(e.target.value)}
                    placeholder={existingDraft
                        ? "Describe how you'd like this email rewritten..."
                        : "Describe the email you want to write... e.g. 'Follow up on our demo call, suggest scheduling a technical deep-dive next week'"
                    }
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 resize-none"
                    autoFocus
                />

                {/* Context badges */}
                <div className="flex flex-wrap gap-1.5">
                    {contactInfo?.disc_profile && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-stone-100 text-stone-600">
                            <DISCBadge profile={contactInfo.disc_profile} size="sm" className="w-3.5 h-3.5 text-[8px]" />
                            DISC: {contactInfo.disc_profile}-type
                        </span>
                    )}
                    {contactInfo && !contactInfo.disc_profile && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-stone-100 text-stone-400">
                            <User className="w-3 h-3" /> No DISC profile
                        </span>
                    )}
                    {hasVoiceProfile && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-600">
                            <Mic className="w-3 h-3" /> Voice Profile Active
                        </span>
                    )}
                    {!hasVoiceProfile && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-600">
                            <Mic className="w-3 h-3" /> No voice profile
                        </span>
                    )}
                    {dealId && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-600">
                            <Target className="w-3 h-3" /> Deal context
                        </span>
                    )}
                    {accountId && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-600">
                            <Briefcase className="w-3 h-3" /> Company context
                        </span>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2">
                    <button onClick={onClose} className="text-xs text-stone-500 hover:text-stone-700" type="button">Cancel</button>
                    <Button size="sm" onClick={handleGenerate} disabled={!prompt.trim() && !existingDraft}>
                        <Sparkles className="w-3.5 h-3.5 mr-1" /> Generate
                    </Button>
                </div>
            </div>
        );
    }

    // ─── Loading State (with progressive streaming preview) ────────────────
    if (loading) {
        return (
            <div className="bg-stone-50 border-b border-stone-200 p-4 space-y-3">
                <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-stone-400 animate-spin" />
                    <span className="text-sm text-stone-500">Writing your email...</span>
                    {contactInfo?.disc_profile && (
                        <span className="text-xs text-stone-400">Adapting to {contactInfo.disc_profile}-type style</span>
                    )}
                </div>
                {streamingResult?.subject && (
                    <div>
                        <label className="block text-[10px] font-medium text-stone-500 uppercase mb-1">Subject</label>
                        <p className="text-sm text-stone-700 px-3 py-1.5">{streamingResult.subject}</p>
                    </div>
                )}
                {streamingResult?.body && (
                    <div className="bg-white border border-stone-200 rounded-lg p-3 max-h-48 overflow-y-auto opacity-70">
                        <div className="prose prose-sm prose-stone max-w-none text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(streamingResult.body) }} />
                    </div>
                )}
            </div>
        );
    }

    // ─── Review State ───────────────────────────────────────────────────────
    return (
        <div className="bg-stone-50 border-b border-stone-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm font-medium text-stone-700">Generated Email</span>
                </div>
                <button onClick={onClose} className="p-1 text-stone-400 hover:text-stone-600" type="button">
                    <X className="w-4 h-4" />
                </button>
            </div>

            {/* Subject */}
            <div>
                <label className="block text-[10px] font-medium text-stone-500 uppercase mb-1">Subject</label>
                <input
                    value={editSubject}
                    onChange={e => setEditSubject(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                />
            </div>

            {/* Body preview */}
            <div className="bg-white border border-stone-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                <div className="prose prose-sm prose-stone max-w-none text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result?.body || '') }} />
            </div>

            {/* Coaching notes */}
            {result?.coaching_notes?.length > 0 && (
                <div className="space-y-1">
                    {result.coaching_notes.map((note, i) => (
                        <p key={i} className="text-xs text-stone-500 flex items-start gap-1.5">
                            <span className="text-amber-500 mt-0.5 flex-shrink-0">+</span> {note}
                        </p>
                    ))}
                </div>
            )}

            {/* DISC context */}
            {result?.disc_context && (
                <p className="text-xs text-stone-400 italic">{result.disc_context.summary}</p>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-1">
                <Button variant="ghost" size="sm" onClick={handleRegenerate} className="text-xs">
                    <RefreshCw className="w-3 h-3 mr-1" /> Regenerate
                </Button>
                <div className="flex gap-2">
                    <button onClick={onClose} className="text-xs text-stone-500 hover:text-stone-700" type="button">Discard</button>
                    <Button size="sm" onClick={handleInsert}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Insert into Email
                    </Button>
                </div>
            </div>
        </div>
    );
}
